import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Card from '../../components/common/Card';
import Input from '../../components/common/Input';
import MaskedInput from '../../components/common/MaskedInput';
import Select from '../../components/common/Select';
import Textarea from '../../components/common/Textarea';
import Radio from '../../components/common/Radio';
import Button from '../../components/common/Button';
import { adminService } from '../../services/adminService';
import { useToast } from '../../contexts/ToastContext';
import { unmask } from '../../utils/maskHelpers';

const EMPTY = {
  name: '',
  slug: '',
  company: '',
  cnpj: '',
  description: '',
  businessType: 'seller',
  status: 'trial',
  subdomain: '',
  customDomain: '',
  whatsappNumber: '',
  contactEmail: '',
  contactPhone: '',
};

const AdminCreateStore = () => {
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [ownerMode, setOwnerMode] = useState('new');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [ownerForm, setOwnerForm] = useState({ email: '', name: '', phone: '', cpf: '' });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setOwner = (k, v) => setOwnerForm((p) => ({ ...p, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      error('Nome da loja é obrigatório');
      return;
    }
    if (form.businessType === 'catalog' && !form.whatsappNumber.trim()) {
      error('WhatsApp é obrigatório em lojas catalog');
      return;
    }

    // Campos com máscara precisam ir só com dígitos pro backend.
    const cleaned = { ...form };
    if (cleaned.cnpj) cleaned.cnpj = unmask(cleaned.cnpj);
    if (cleaned.contactPhone) cleaned.contactPhone = unmask(cleaned.contactPhone);
    if (cleaned.whatsappNumber) cleaned.whatsappNumber = unmask(cleaned.whatsappNumber);

    const payload = {
      ...Object.fromEntries(Object.entries(cleaned).filter(([, v]) => v !== '')),
      owner:
        ownerMode === 'existing'
          ? { userId: Number(ownerUserId) }
          : {
              email: ownerForm.email.trim(),
              name: ownerForm.name.trim(),
              phone: ownerForm.phone ? unmask(ownerForm.phone) : null,
              cpf: ownerForm.cpf ? unmask(ownerForm.cpf) : null,
            },
    };

    setSaving(true);
    try {
      const res = await adminService.createStore(payload);
      success(
        res.tempPasswordSent
          ? `Loja "${res.store.name}" criada. Email de acesso enviado pra ${res.owner.email}.`
          : `Loja "${res.store.name}" criada pra ${res.owner.email}.`
      );
      navigate('/admin-reviter/lojas');
    } catch (e) {
      error(e.details?.map((d) => d.message).join('; ') || e.message || 'Erro ao criar loja');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Link
        to="/admin-reviter/lojas"
        className="inline-flex items-center gap-2 text-sm text-app-text-secondary hover:text-app-text mb-4 transition-colors"
      >
        <ArrowLeft size={16} /> Voltar para lojas
      </Link>
      <h1 className="text-3xl font-extrabold text-app-text mb-1">Nova loja</h1>
      <p className="text-sm text-app-text-secondary mb-6">
        Cria a loja e vincula (ou cria) o usuário owner.
      </p>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card className="p-6 space-y-4">
          <h2 className="text-lg font-bold text-app-text">Dados da loja</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Nome"
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              required
            />
            <Input
              label="Slug"
              value={form.slug}
              onChange={(e) => set('slug', e.target.value.toLowerCase())}
              placeholder="slug-da-loja"
              helpText="Opcional — gerado do nome se vazio"
            />
            <Input
              label="Razão social"
              value={form.company}
              onChange={(e) => set('company', e.target.value)}
            />
            <MaskedInput
              label="CNPJ"
              mask="cnpj"
              value={form.cnpj}
              onChange={(e) => set('cnpj', e.target.value)}
              placeholder="00.000.000/0001-00"
            />
          </div>
          <Textarea
            label="Descrição"
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Modelo de negócio"
              value={form.businessType}
              onChange={(e) => set('businessType', e.target.value)}
              options={[
                { value: 'seller', label: 'Seller (e-commerce direto)' },
                { value: 'reseller', label: 'Reseller (com revendedores)' },
                { value: 'catalog', label: 'Catálogo (WhatsApp)' },
              ]}
              required
            />
            <Select
              label="Status inicial"
              value={form.status}
              onChange={(e) => set('status', e.target.value)}
              options={[
                { value: 'trial', label: 'Trial' },
                { value: 'active', label: 'Ativa' },
                { value: 'suspended', label: 'Suspensa' },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Subdomínio"
              value={form.subdomain}
              onChange={(e) => set('subdomain', e.target.value.toLowerCase())}
              placeholder="minhaloja"
              helpText="Acesso via minhaloja.reviter.com"
            />
            <Input
              label="Domínio próprio"
              value={form.customDomain}
              onChange={(e) => set('customDomain', e.target.value.toLowerCase())}
              placeholder="loja.com.br"
            />
          </div>
          {form.businessType === 'catalog' && (
            <MaskedInput
              label="WhatsApp"
              mask="phone"
              value={form.whatsappNumber}
              onChange={(e) => set('whatsappNumber', e.target.value)}
              placeholder="(11) 98765-4321"
              required
              helpText="Obrigatório em lojas catalog"
            />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input
              label="Email de contato"
              type="email"
              value={form.contactEmail}
              onChange={(e) => set('contactEmail', e.target.value)}
            />
            <MaskedInput
              label="Telefone de contato"
              mask="phone"
              value={form.contactPhone}
              onChange={(e) => set('contactPhone', e.target.value)}
              placeholder="(11) 98765-4321"
            />
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <h2 className="text-lg font-bold text-app-text">Owner da loja</h2>
          <div className="flex flex-wrap gap-4">
            <Radio
              name="ownerMode"
              value="new"
              label="Criar owner novo (enviamos email de acesso)"
              checked={ownerMode === 'new'}
              onChange={() => setOwnerMode('new')}
            />
            <Radio
              name="ownerMode"
              value="existing"
              label="Vincular user existente"
              checked={ownerMode === 'existing'}
              onChange={() => setOwnerMode('existing')}
            />
          </div>

          {ownerMode === 'existing' ? (
            <Input
              label="User ID"
              type="number"
              value={ownerUserId}
              onChange={(e) => setOwnerUserId(e.target.value)}
              placeholder="123"
              required
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Email"
                type="email"
                value={ownerForm.email}
                onChange={(e) => setOwner('email', e.target.value)}
                required
              />
              <Input
                label="Nome"
                value={ownerForm.name}
                onChange={(e) => setOwner('name', e.target.value)}
                required
              />
              <MaskedInput
                label="Telefone"
                mask="phone"
                value={ownerForm.phone}
                onChange={(e) => setOwner('phone', e.target.value)}
                placeholder="(11) 98765-4321"
              />
              <MaskedInput
                label="CPF"
                mask="cpf"
                value={ownerForm.cpf}
                onChange={(e) => setOwner('cpf', e.target.value)}
                placeholder="000.000.000-00"
              />
            </div>
          )}
        </Card>

        <div className="flex gap-2 justify-end">
          <Link to="/admin-reviter/lojas">
            <Button variant="subtle" type="button">
              Cancelar
            </Button>
          </Link>
          <Button type="submit" loading={saving} size="lg">
            Criar loja
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdminCreateStore;
